<template>
    <div class="wrapper">
        <b-modal
            id="termsOfUse"
            ok-only
            ok-title="Close"
            hide-header-close
            no-close-on-esc
            no-close-on-backdrop
            :scrollable="true"
            size="lg"
            :title="$translate('terms_of_use')"
            @ok="ok"
        >
            <terms-of-use />
        </b-modal>

        <b-modal
            id="yesNo"
            :click-to-close="false"
            ok-text="Yes"
            no-close-on-esc
            no-close-on-backdrop
            hide-header-close
            cancel-text="Cancel"
            @ok="ok"
            @cancel="cancel"
        >
            <div slot="modal-header">
                <h5 class="modal-title text-truncate">{{ $translate(yesNoHeader) }}</h5>
            </div>

            <div class="row">
                <div class="col-sm-12 my-3">
                    {{ $translate(yesNoMessage) }}
                </div>
            </div>
        </b-modal>

        <b-modal
            id="error"
            :click-to-close="false"
            ok-title="Close"
            ok-only
            no-close-on-esc
            no-close-on-backdrop
            hide-header-close
            @ok="ok"
            @cancel="cancel"
        >
            <div slot="modal-header">
                <h5 class="modal-title">{{ errorTitle ? errorTitle : 'Error!' }}</h5>
            </div>

            <div style="overflow: auto; height: 100%">
                <div class="px-3 pb-2">
                    <div class="mt-4 mb-1">
                        <div
                            class="col-sm-12 my-3"
                            v-html="$translate(errorMessage ? errorMessage : 'error')"
                        ></div>

                        <div class="display-flex">
                            <b-button class="ml-auto" @click="signOut('error')">
                                {{ $translate('sign_out') }}
                            </b-button>

                            <b-button class="ml-2" @click="navigateHome('error')">
                                {{ $translate('home') }}
                            </b-button>
                        </div>
                    </div>
                </div>
            </div>
        </b-modal>

        <b-modal
            id="about"
            ok-title="Close"
            size="lg"
            ok-only
            no-close-on-backdrop
            hide-footer
            modal-class="about-us-modal"
            @close="cancel"
        >
            <div slot="modal-title">
                <h5 class="modal-title">{{ $translate('about') }}</h5>
            </div>
            <about-us :version="version" />
        </b-modal>

        <b-modal
            id="settings"
            size="lg"
            no-close-on-esc
            hide-header-close
            no-close-on-backdrop
            :title="$translate('settings')"
            :ok-title="$translate('save')"
            :ok-disabled="disableOKButton"
            :scrollable="true"
            @ok="saveSettings"
            @cancel="cancel"
        >
            <settings-form
                v-if="settingsAreOpen"
                ref="settings-form"
                @ok="ok(null, false, 'settings')"
                @setDisableOKButton="setDisableOKButton"
            />
        </b-modal>

        <b-modal
            id="scoreInfo"
            ok-only
            ok-title="Close"
            hide-header-close
            no-close-on-esc
            hide-header
            no-close-on-backdrop
            :scrollable="true"
            size="xl"
            @ok="ok"
        >
            <score-info v-if="well" :well="well" />
        </b-modal>

        <b-modal
            id="video_tutorial"
            ok-only
            ok-title="Close"
            hide-header-close
            no-close-on-esc
            hide-header
            no-close-on-backdrop
            :scrollable="true"
            size="xl"
            @ok="ok"
        >
            <div slot="modal-header" class="display-none"></div>
            <div class="tutorials">
                <div
                    v-for="tutorial in componentTutorial"
                    :key="tutorial.title"
                    class="h-100 w-100"
                >
                    <div class="mb-3 text-bold text-size-13">
                        {{ tutorial.title }}
                    </div>

                    <div class="w-100 h-100 align-items-center mb-4">
                        <video class="h-100 w-100 mx-auto" :controls="true" preload="auto">
                            <source :src="tutorial.videoUrl" type="video/mp4" />
                        </video>
                    </div>
                </div>
            </div>
        </b-modal>

        <b-modal
            id="blast_collapses"
            ok-title="Close"
            size="xl"
            hide-footer
            centered
            hide-header
            no-close-on-backdrop
            @ok="ok"
            @cancel="cancel"
        >
            <b-button
                variant="transparent"
                class="position-absolute right-2 top-2"
                @click="cancel(null, null, 'blast_collapses')"
            >
                <i class="fa fa-close 2x" />
            </b-button>

            <div class="blast-collapses-dialog">
                <div class="px-3 pt-3 text-light-1">
                    <div class="p-4 display-flex">
                        <div v-if="blastCollapses" class="flex-column blast-collapse-details">
                            <div class="display-flex text-size-15 text-light-2 mb-3">
                                <span class="mr-3">{{ $translate('num_of_collapses') }}</span>
                                <span>{{ blastCollapses.length }}</span>
                            </div>

                            <div
                                v-for="(collapse, index) in blastCollapses"
                                :key="index"
                                class="text-size-10 text-light-2 py-1 px-2 collapse-row display-flex align-items-center"
                                :class="selectedBlastCollapse === collapse ? 'selected' : ''"
                            >
                                <span class="mr-3">
                                    {{ $translate('collapse') }} {{ index + 1 }}
                                    {{ $translate('at') }}
                                    <b-button
                                        variant="link"
                                        size="sm"
                                        class="text-underline pb-1"
                                        @click="jumpToVideoAtRun(collapse.collapsedIndex)"
                                        >{{ runs[collapse.collapsedIndex].time }}h</b-button
                                    >
                                </span>

                                <span>{{ collapse.percentage }}%</span>
                            </div>
                        </div>

                        <div class="flex-column">
                            <div
                                v-if="selectedBlastCollapse && runs && segmentations"
                                class="display-flex blast-collapse-images ml-5"
                            >
                                <div class="mr-2 position-relative">
                                    <img :src="selectedBlastCollapse.expandedImageUrl" alt="" />

                                    <well-time-view
                                        :minimal="true"
                                        class="position-absolute left-3 bottom-3 text-size-15 text-bold"
                                        :current-well-time_h="
                                            runs[selectedBlastCollapse.expandedIndex].time
                                        "
                                    />

                                    <well-graphical-segmentations
                                        v-if="segmentations"
                                        class="w-100 h-100 rounded-top-lg"
                                        :always-show="true"
                                        :segmentations="segmentations"
                                        :current-run-index="selectedBlastCollapse.expandedIndex - 1"
                                    />
                                </div>

                                <div class="position-relative">
                                    <img :src="selectedBlastCollapse.collapsedImageUrl" alt="" />

                                    <well-time-view
                                        :minimal="true"
                                        class="position-absolute left-3 bottom-3 text-size-15 text-bold"
                                        :current-well-time_h="
                                            runs[selectedBlastCollapse.collapsedIndex].time
                                        "
                                    />

                                    <well-graphical-segmentations
                                        v-if="segmentations"
                                        class="w-100 h-100 rounded-top-lg"
                                        :always-show="true"
                                        :segmentations="segmentations"
                                        :current-run-index="
                                            selectedBlastCollapse.collapsedIndex - 1
                                        "
                                    />
                                </div>
                            </div>

                            <div class="justify-center mt-2">
                                <b-button
                                    variant="transparent"
                                    size="lg"
                                    class="mr-3 ml-5"
                                    @click="changeSelectedBastCollapse(true)"
                                >
                                    <i class="fa fa-chevron-left" />
                                </b-button>

                                <b-button
                                    variant="transparent"
                                    size="lg"
                                    @click="changeSelectedBastCollapse(false)"
                                >
                                    <i class="fa fa-chevron-right" />
                                </b-button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </b-modal>

        <b-modal
            id="images_overlay"
            ok-title="Close"
            size="xl"
            ok-only
            no-close-on-backdrop
            hide-footer
            modal-class="images-overlay-b-modal"
            @close="cancel"
        >
            <div slot="modal-title">
                <h5 class="modal-title">
                    {{ $translate('view_annotations') }}
                    <span v-if="well" class="id-text"
                        >{{ $translate('e_id') }} {{ well.wellIndex + 1 }}</span
                    >
                </h5>
            </div>
            <images-overlay-modal :well="well" />
        </b-modal>
    </div>
</template>

<script>
import pjson from '../../../package.json'
import EventDispatcher from '../../business/services/EventDispatcher'
import AboutUs from './modals/AboutUs'
import ScoreInfo from './modals/ScoreInfo'
import TermsOfUse from './modals/TermsOfUse'
import WellTimeView from './common/WellTimeView'
import SettingsForm from './modals/SettingsForm'
import ImagesOverlayModal from './modals/ImagesOverlayModal'
import WellGraphicalSegmentations from './WellGraphicalSegmentations'

const defaults = {
    blastCollapses: null,
    segmentations: null,
    selectedBlastCollapse: null,
    runs: null,
    componentTutorial: null,
    well: null
}

export default {
    name: 'ModalPanels',
    components: {
        AboutUs,
        SettingsForm,
        ImagesOverlayModal,
        WellGraphicalSegmentations,
        WellTimeView,
        ScoreInfo,
        TermsOfUse
    },
    data: () => ({
        modals: [],
        errorTitle: null,
        errorMessage: null,
        yesNoMessage: '',
        yesNoHeader: '',
        disableOKButton: false,
        ...Object.assign({}, defaults)
    }),
    computed: {
        version() {
            return pjson.version
        },
        settingsAreOpen() {
            return this.isOpen('settings')
        }
    },
    methods: {
        show(id, onShow, okCallback, cancelCallback, focusRef) {
            if (this.isOpen(id)) {
                this.reset()
                return
            }

            if (onShow) {
                onShow(this.$data)
            }

            if (focusRef) {
                const $ref = this.$refs[focusRef]

                if ($ref) {
                    window.setTimeout(() => {
                        $ref.focus()
                    }, 10)
                }
            }

            this.modals.push({ id, okCallback, cancelCallback })
            this.$bvModal.show(id)
        },
        reset() {
            window.setTimeout(() => {
                Object.assign(this.$data, defaults)
            }, 200)
        },
        ok(bvModalEvt, doNotReset, modalId = null) {
            if (bvModalEvt) {
                bvModalEvt.preventDefault()
            }

            const modal = this.findModal(modalId || bvModalEvt.componentId)

            if (modal) {
                window.setTimeout(async () => {
                    if (modal.okCallback) {
                        await modal.okCallback(this.$data, this.$refs, arguments)
                    }

                    this.$bvModal.hide(modal.id)
                    this.modals.remove(modal)

                    if (!doNotReset) {
                        this.reset()
                    }
                }, 100)
            }
        },
        findModal(id) {
            return this.modals.find(modal => modal.id === id)
        },
        cancel(bvModalEvt, doNotReset, modalId = null) {
            const modal = this.findModal(modalId || bvModalEvt.componentId)

            if (modal) {
                if (modal.cancelCallback) {
                    modal.cancelCallback(this.$data, this.$refs, arguments)
                }

                if (!bvModalEvt) {
                    this.$bvModal.hide(modal.id)
                }

                this.modals.remove(modal)

                if (!doNotReset) {
                    this.reset()
                }
            }
        },
        closeLast() {
            const modal = this.modals.length > 0 ? this.modals[this.modals.length - 1] : null

            if (modal) {
                this.cancel(modal.id, modal.id === 'addComment')

                return true
            } else {
                return false
            }
        },
        isOpen(id) {
            return id ? !!this.findModal(id) : this.modals.length > 0
        },
        signOut(modalId) {
            this.$mainController.signOut()

            this.ok(null, false, modalId)
        },
        navigateHome(modalId) {
            this.$store.dispatch('navigation/navigateHome')

            this.ok(null, false, modalId)
        },
        saveSettings(bvModalEvt) {
            if (bvModalEvt) {
                bvModalEvt.preventDefault()
            }
            this.$refs['settings-form'].save()
        },
        setDisableOKButton(isVisible) {
            this.disableOKButton = isVisible
        },
        jumpToVideoAtRun(runIndex) {
            this.cancel(null, null, 'blast_collapses')

            EventDispatcher.$emit('SKIP_VIDEO_TO_RUN', runIndex)
        },
        changeSelectedBastCollapse(previous) {
            const indexOfCurrent = this.blastCollapses.indexOf(this.selectedBlastCollapse)

            this.selectedBlastCollapse = this.blastCollapses.safeGet(
                indexOfCurrent + (previous ? -1 : 1)
            )
        }
    }
}
</script>
